/**
 * A Card whose body folds away behind its title, the way Activity always has.
 *
 * The creative page had grown to eleven stacked panels, which on a phone is a very
 * long scroll before you reach the thing you came for. Everything collapses now and
 * starts closed, so the page opens as a list of headings you can aim at.
 *
 * Each section remembers its choice per PAGE, for the length of the visit. It used to
 * remember per section name in localStorage, so opening Assets on one creative opened
 * it on every creative afterwards, for ever, on that device: you could never land on a
 * piece and see the short list of headings the page was designed to open as.
 * Storage can throw (private mode, blocked site data), so every access is guarded and
 * falls back to the default rather than taking the panel down with it.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

/**
 * The same remembered open/closed state, for panels that own their header because
 * it carries buttons (Save, Discard, a status pill). Those cannot be folded into
 * CollapsibleCard's plain header without losing the controls.
 */
export function useCollapsed(storageKey: string, defaultOpen = false) {
  // Scoped to the page being looked at, so one creative's layout never becomes every
  // creative's, and kept in sessionStorage so tomorrow starts clean.
  const { pathname } = useLocation();
  const key = `${storageKey}@${pathname}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = sessionStorage.getItem(key);
      return v === null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });
  // A different page means a different question; fall back to the default there.
  useEffect(() => {
    try {
      const v = sessionStorage.getItem(key);
      setOpen(v === null ? defaultOpen : v === '1');
    } catch { /* storage unavailable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    try {
      sessionStorage.setItem(key, open ? '1' : '0');
    } catch { /* storage unavailable */ }
  }, [open, key]);
  // a notification link opens its section (lib/deep-link): window event with the fold key
  useEffect(() => {
    const on = (e: Event) => { if ((e as CustomEvent<string>).detail === storageKey) setOpen(true); };
    window.addEventListener('kaf:open-section', on);
    return () => window.removeEventListener('kaf:open-section', on);
  }, [storageKey]);
  return { open, toggle: () => setOpen((o) => !o) };
}

/** The chevron a panel puts at the head of its own title row. */
export function CollapseChevron({ open }: { open: boolean }) {
  return open
    ? <ChevronDown size={14} className="text-muted shrink-0" />
    : <ChevronRight size={14} className="text-muted shrink-0" />;
}

export default function CollapsibleCard({
  title,
  icon,
  badge,
  storageKey,
  defaultOpen = false,
  className,
  headerClassName,
  children,
}: {
  title: ReactNode;
  icon?: ReactNode;
  /** short count or status shown on the right of the header, visible when closed */
  badge?: ReactNode;
  /** omit to make the section forget its state between visits */
  storageKey?: string;
  defaultOpen?: boolean;
  className?: string;
  headerClassName?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(() => {
    if (!storageKey) return defaultOpen;
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {
      /* storage unavailable - the choice just does not persist */
    }
  }, [open, storageKey]);

  return (
    <Card className={className}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn('w-full flex items-center gap-2 text-left group', open && 'mb-3', headerClassName)}
      >
        {open
          ? <ChevronDown size={14} className="text-muted group-hover:text-muted-strong shrink-0" />
          : <ChevronRight size={14} className="text-muted group-hover:text-muted-strong shrink-0" />}
        {icon}
        <h3 className="font-medium text-muted-strong text-sm">{title}</h3>
        {badge !== undefined && badge !== null && (
          <span className="text-xs text-muted ml-auto shrink-0">{badge}</span>
        )}
      </button>
      {open && children}
    </Card>
  );
}
